import React, { useEffect } from 'react';
import { Box, Chip, Divider, Grid2 as Grid, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubscriptionById } from '../../api/subscriptions/subscriptionSlice';
import { fetchOrganizationById, selectOrganizationById } from '../../api/organizations/organizationSlice';
import { fetchPlanById, selectPlanById } from '../../api/plans/planSlice';
import InvoiceTable from './InvoiceTable';
import SubscriptionUsage from './Subscription Usage';
import { getStatusColor, getStatusLabel } from '../../utils/statues';
import appConfig from '../../config/appConfig';

const SubscriptionDetails = () => {
  const { id } = useParams();
  const { selectedSubscription } = useSelector((state) => state.subscriptions);
  let organization = useSelector(selectOrganizationById(selectedSubscription?.organization_id));
  let plan = useSelector(selectPlanById(selectedSubscription?.plan_id));
  const { invoices } = useSelector((state) => state.invoices);

  let dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchSubscriptionById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedSubscription) {
      dispatch(fetchOrganizationById(selectedSubscription.organization_id));
      dispatch(fetchPlanById(selectedSubscription.plan_id));
    }
  }, [dispatch, selectedSubscription]);

  if (!selectedSubscription) {
    return (
      <Box elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No subscription details found
        </Typography>
      </Box>
    );
  }

  return (
    <Box elevation={3} sx={{ p: 5 }}>
      <Typography variant="h7" color="blue" gutterBottom>
        Subscription
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid>
          <Typography variant="h7">{organization.email} </Typography>
          <Typography variant="title">on {plan?.name} </Typography>
        </Grid>
        <Grid sx={{ ml: '-5px', mb: '2px' }}>
          <Chip
            label={getStatusLabel(selectedSubscription.status)}
            color={getStatusColor(selectedSubscription.status)}
            size="small"
          />
        </Grid>
      </Grid>
      <Typography variant="h6" sx={{ mt: '2rem' }}>
        Subscription Details
      </Typography>
      <Divider></Divider>
      <Grid container sx={{ mt: '1.5rem' }} spacing={3}>
        <Grid container size={{ sm: 12, md: 6 }} justifyContent={'space-between'}>
          <Grid>
            <Typography variant="label">Customer:</Typography>
          </Grid>

          <Grid>
            <Link to={`/organizations`} style={{ textDecoration: 'none', color: 'blue' }}>
              {organization.name}
            </Link>
          </Grid>
        </Grid>
        <Grid container size={{ sm: 12, md: 6 }} justifyContent={'space-between'}>
          <Grid>
            <Typography variant="label">Payment Buffer:</Typography>
          </Grid>
          <Grid>
            <Typography>0</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ sm: 12, md: 6 }} justifyContent={'space-between'}>
          <Grid>
            <Typography variant="label">Created on:</Typography>
          </Grid>
          <Grid>
            <Typography variant="date">
              {moment(selectedSubscription.createdAt).tz(appConfig.timezoneName).format('lll')}
            </Typography>
          </Grid>
        </Grid>
        <Grid container size={{ sm: 12, md: 6 }} justifyContent={'space-between'}>
          <Grid>
            <Typography variant="label">Current Period:</Typography>
          </Grid>
          <Grid>
            <Typography variant="date">
              {moment(selectedSubscription.current_period_start * 1000)
                .tz(appConfig.timezoneName)
                .format('lll')}
              &nbsp;- &nbsp;
              {moment(selectedSubscription.current_period_end * 1000)
                .tz(appConfig.timezoneName)
                .format('lll')}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: '2rem' }}>
        Cancellation Details
      </Typography>
      <Divider></Divider>
      <Grid container sx={{ mt: '1.5rem' }}>
        <Grid container size={{ sm: 12, md: 6 }} alignItems="center" spacing={3}>
          <Grid>
            <Typography variant="label">Expiry:</Typography>
          </Grid>
          <Grid>
            <Typography variant="date">
              {selectedSubscription.end_date
                ? moment(selectedSubscription.end_date * 1000)
                    .tz(appConfig.timezoneName)
                    .format('lll')
                : 'NA'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Typography variant="h6" sx={{ mt: '2rem', fontWeight: 'bold' }} gutterBottom>
        Invoices
      </Typography>
      <Divider sx={{ mt: '10px' }} />
      <InvoiceTable subscription={selectedSubscription} planType={plan?.type} />
      <Typography variant="h6" sx={{ mt: '1.5rem', fontWeight: 'bold' }} gutterBottom>
        Usage Tracking
      </Typography>
      <Divider sx={{ mt: '10px' }} />
      <SubscriptionUsage subscription={selectedSubscription} invoices={invoices} />
    </Box>
  );
};

export default SubscriptionDetails;
